import React, { useState, useEffect, useRef } from "react";
import { Form, Badge, Button } from "react-bootstrap";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';

interface BranchItem {
  id: number;
  name: string;
  flag_id_user?: boolean;
}

interface BranchSelectMultiProps {
  branches: BranchItem[];
  selectedIds: number[];
  onSelect: (selected: number[]) => void;
  label: string;
}

const limparNome = (nome: string) => nome.replace(/\s*\(.*?\)\s*/g, "").trim();

const BranchSelectMulti: React.FC<BranchSelectMultiProps> = ({ branches, selectedIds, onSelect, label }) => {
  const { t } = useTranslation();

  const [searchTerm, setSearchTerm] = useState("");
  const [filtered, setFiltered] = useState<BranchItem[]>([]);
  const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);
  const [showDropdown, setShowDropdown] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    // Inicialmente, selecionar os que têm flag_id_user = true, se selectedIds estiver vazio
    if (selectedIds.length === 0) {
      const preSelecionadas = branches.filter(b => b.flag_id_user).map(b => b.id);
      onSelect(preSelecionadas);
    }
  }, [branches]);

  useEffect(() => {
    const term = searchTerm.toLowerCase();
    const result = branches
      .filter((b) => b.name.toLowerCase().includes(term))
      .sort((a, b) => limparNome(a.name).localeCompare(limparNome(b.name)));
    setFiltered(result);
    setHighlightedIndex(0);
  }, [searchTerm, branches]);

  const toggleSelect = (id: number) => {
    if (selectedIds.includes(id)) {
      onSelect(selectedIds.filter((gid) => gid !== id));
    } else {
      onSelect([...selectedIds, id]);
    }
    setSearchTerm("");
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown") setHighlightedIndex((prev) => Math.min(prev + 1, filtered.length - 1));
    else if (e.key === "ArrowUp") setHighlightedIndex((prev) => Math.max(prev - 1, 0));
    else if (e.key === "Enter") {
      e.preventDefault();
      if (highlightedIndex >= 0 && highlightedIndex < filtered.length) {
        toggleSelect(filtered[highlightedIndex].id);
      }
    }
  };

  const handleBlur = () => {
    setTimeout(() => setShowDropdown(false), 200);
  };

  const handleSelectAll = () => {
    const allIds = branches.map((b) => b.id);
    onSelect(allIds);
  };

  const handleClearAll = () => {
    onSelect([]);
  };

  const handleRemove = (id: number) => {
    onSelect(selectedIds.filter((gid) => gid !== id));
  };

  return (
    <Form.Group style={{ position: "relative" }}>
      <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.5rem" }}>
        <Form.Label style={{ marginBottom: 0 }}>{label}</Form.Label>
        <Button size="sm" variant="outline-primary" onClick={handleSelectAll}>{t("SELECT_ALL")}</Button>
        <Button size="sm" variant="outline-danger" onClick={handleClearAll}>{t("CLEAN")}</Button>
      </div>

      <Form.Control
        type="text"
        placeholder={t("TYPE_OR_SELECT_LABEL", { label })}
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        onFocus={() => setShowDropdown(true)}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        ref={inputRef}
      />

      {showDropdown && (
        <div style={{
          border: "1px solid #ccc",
          maxHeight: 200,
          overflowY: "auto",
          background: "#fff",
          position: "absolute",
          zIndex: 10,
          width: inputRef.current?.offsetWidth || "100%"
        }}>
          {filtered.map((item, index) => (
            <div
              key={item.id}
              onClick={() => toggleSelect(item.id)}
              style={{
                padding: 8,
                cursor: "pointer",
                backgroundColor: highlightedIndex === index ? "#e9ecef" : "white",
                fontWeight: selectedIds.includes(item.id) ? "bold" : "normal"
              }}
              onMouseEnter={() => setHighlightedIndex(index)}
            >
              {item.name}
            </div>
          ))}
          {filtered.length === 0 && (
            <div style={{ padding: 8, color: "#999" }}>{t("NO_ITEMS_FOUND")}</div>
          )}
        </div>
      )}

      <div className="mt-2" style={{
        display: "grid",
        gridTemplateColumns: "repeat(5, 1fr)",
        gap: "0.5rem"  // aumenta espaço entre colunas
      }}>
        {[...selectedIds]
          .map(id => branches.find((b) => b.id === id))
          .filter((b): b is BranchItem => !!b)
          .sort((a, b) => limparNome(a.name).localeCompare(limparNome(b.name)))
          .map((item) => (
            <div key={item.id} style={{ width: "85%" }}>
              <Badge
                pill
                bg="light"
                style={{
                  width: "100%",
                  padding: "4px 10px",
                  fontSize: "0.75rem",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  borderRadius: "0.75rem",
                  color: "#004085", // azul escuro padrão bootstrap para texto
                  border: "1px solid #004085" // opcional: borda azul para combinar
                }}
              >
                <span
                  style={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    textAlign: "left",          // alinhamento horizontal do texto
                    maxWidth: "calc(100% - 24px)" // evita que o botão 'x' empurre o texto
                  }}>
                  {limparNome(item.name)}
                </span>
                <FontAwesomeIcon
                  icon={faTimes}
                  style={{ marginLeft: "10px", flexShrink: 0, cursor: "pointer" }}
                  onClick={() => handleRemove(item.id)}
                />
              </Badge>
            </div>
          ))}
      </div>

    </Form.Group>
  );
};

export default BranchSelectMulti;